import { createTheme } from '@mui/material/styles';
import { koKR } from '@mui/material/locale';

// MUI 테마 설정 / MUI theme configuration
// MUI Palette 타입 확장 / Extend MUI Palette type
declare module '@mui/material/styles' {
  interface Palette {
    custom: Palette['primary'];
  }
  interface PaletteOptions {
    custom?: PaletteOptions['primary'];
  }
  interface PaletteColor {
    darker?: string;
    lighter?: string;
  }
  interface SimplePaletteColorOptions {
    darker?: string;
    lighter?: string;
  }

  // 타이포그래피 커스텀 variant 추가 / Add custom typography variant
  interface TypographyVariants {
    subtitle3: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    subtitle3?: React.CSSProperties;
  }
}

// Typography prop에서 subtitle3 사용 허용 / Allow subtitle3 in Typography props
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle3: true;
  }
}

// Button 커스텀 variant 추가 / Add custom Button variant
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    tab: true; // 탭 버튼용 커스텀 variant / Custom variant for tab buttons
  }
}
export const theme = createTheme(
  {
    palette: {
      primary: {
        main: '#ee2b49', // 메인 컬러 변경
        lighter: '#ffe5e9', // 연한 빨강색
      },
      secondary: {
        main: '#485362',
        light: '#6a7484',
        dark: '#3a4149',
        darker: '#485362', // 원하는 색상으로 설정
      },
      grey: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#eeeeee',
        300: '#e0e0e0',
        400: '#bdbdbd',
        500: '#9e9e9e',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      },
    },
    typography: {
      htmlFontSize: 15, // 1rem = 15px로 설정 / Set base rem
      fontFamily:
        '"Noto Sans KR", "NotoSansKR", "Roboto", "Helvetica", "Arial", sans-serif',
      // 커스텀 subtitle3 정의 / Define custom subtitle3
      subtitle3: {
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 1.4,
      },
    },
    shape: {
      borderRadius: 8, // 기본 border-radius
    },

    components: {
      // 버튼 기본 스타일 / Button default styles
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px', // 기본 border-radius
            textTransform: 'none', // 대문자 변환 제거
            padding: '8px 16px',
            fontWeight: 500,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
        variants: [
          {
            props: { variant: 'tab' },
            style: {
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: 700,
              backgroundColor: 'transparent',
              border: '1px solid #e0e0e0', // grey.300
              color: '#485362', // secondary.darker
              '&:hover': {
                borderColor: '#757575', // grey.600
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              // active 상태 (선택된 탭) / Active state (selected tab)
              '&.Mui-selected': {
                backgroundColor: '#485362', // secondary.darker
                color: '#ffffff',
                borderColor: '#485362',
                '&:hover': {
                  backgroundColor: '#3a4149', // secondary.dark
                },
              },
            },
          },
        ],
      },

      // 카드 기본 스타일 / Card default styles
      MuiCard: {
        styleOverrides: {
          root: {
            // borderRadius: '12px',
            // boxShadow: '0 2px 8px rgba(0,0,0,0.08)',

            // 메시지 박스 스타일 / Message box styles
            '&.message-box': {
              borderRadius: '8px',
              boxShadow: 'none',
              border: 'none',
              maxWidth: '720px',
            },

            // 사용자 메시지 / User message
            '&.user-message': {
              backgroundColor: '#eeeeee', // grey.200
              color: 'rgba(0, 0, 0, 0.87)',
              alignSelf: 'flex-end',
            },

            // AI 메시지 / AI message
            '&.ai-message': {
              backgroundColor: 'transparent',
              alignSelf: 'flex-start',
            },

            // 로딩 카드 / Loading card
            '&.loading-card': {
              backgroundColor: '#f5f5f5', // grey.100
            },
          },
        },
      },

      // 텍스트 필드 기본 스타일 / TextField default styles
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          },
        },
      },

      // 아이콘 버튼 기본 스타일 / IconButton default styles
      MuiIconButton: {
        styleOverrides: {
          root: {
            // 액션 버튼 (복사, 재생성 등) / Action button
            '&.action-button': {
              padding: 0,
              '&:hover': {
                opacity: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },

            // 파일 업로드 버튼 / File upload button
            '&.file-upload-button': {
              padding: '8px',
              '&.active': {
                color: '#ee2b49',
              },
            },

            // 전송 버튼 / Send button
            '&.send-button': {
              padding: '8px',
              '&:disabled': {
                opacity: 0.3,
              },
            },
          },
        },
      },

      // Chip 기본 스타일 / Chip default styles
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '6px',

            // 문자 카운터 칩 / Character counter chip
            '&.character-counter': {
              height: '20px',
              fontSize: '10px',
            },

            // 파일 칩 / File chip
            '&.file-chip': {
              maxWidth: '200px',
            },

            // 번역 상태 칩 / Translation status chip
            '&.translation-status': {
              backgroundColor: 'rgba(238, 43, 73, 0.08)',
              color: '#ee2b49',
            },
          },
        },
      },

      // Paper 기본 스타일 / Paper default styles
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '12px',

            // 채팅 컨테이너 스타일 / Chat container styles
            '&.chat-container': {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              boxShadow: 'none',
            },

            // 입력 영역 / Input area
            '&.input-area': {
              padding: '16px',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: '#ffffff',
            },

            // 번역 영역 / Translation area
            '&.translation-area': {
              marginTop: '8px',
              padding: '12px',
              backgroundColor: 'rgba(238, 43, 73, 0.04)',
              borderRadius: '8px',
              border: '1px solid rgba(238, 43, 73, 0.2)',
            },

            // 에러 영역 / Error area
            '&.error-area': {
              padding: '12px',
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              borderRadius: '8px',
              border: '1px solid rgba(211, 47, 47, 0.2)',
            },
          },
        },
      },

      // Container 기본 스타일 / Container default styles
      MuiContainer: {
        styleOverrides: {
          root: {
            // 캔버스 컨테이너 / Canvas container
            '&.canvas-container': {
              paddingTop: '8px',
              paddingBottom: '8px',
            },
          },
        },
      },

      // Dialog 기본 스타일 / Dialog default styles
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
          },
        },
      },

      // Avatar 기본 스타일 / Avatar default styles
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: '8px',

            // AI 아바타 / AI avatar
            '&.ai-avatar': {
              backgroundColor: 'transparent',
              width: '32px',
              height: '32px',
            },

            // 사용자 아바타 / User avatar
            '&.user-avatar': {
              backgroundColor: '#485362',
              width: '32px',
              height: '32px',
            },
          },
        },
      },

      // Tooltip 기본 스타일 / Tooltip default styles
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: '6px',
            fontSize: '12px',
          },
        },
      },

      // Alert 기본 스타일 / Alert default styles
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
          },
        },
      },

      // Typography 기본 스타일 / Typography default styles
      MuiTypography: {
        styleOverrides: {
          root: {
            wordBreak: 'keep-all', // 한글 줄바꿈 개선

            // 메시지 텍스트 / Message text
            '&.message-text': {
              fontSize: '14px',
              lineHeight: 1.6,
            },

            // 에러 텍스트 / Error text
            '&.error-text': {
              color: '#d32f2f',
              fontSize: '12px',
            },

            // 헬퍼 텍스트 / Helper text
            '&.helper-text': {
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: '12px',
            },

            // 타임스탬프 / Timestamp
            '&.timestamp': {
              color: 'rgba(0, 0, 0, 0.4)',
              fontSize: '11px',
            },

            // 사용자 ID / User ID
            '&.user-id': {
              color: 'rgba(0, 0, 0, 0.4)',
              fontSize: '12px',
              fontFamily: 'monospace',
            },
          },
        },
      },

      // LinearProgress 기본 스타일 / LinearProgress default styles
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            height: '6px',
          },
        },
      },

      // CircularProgress 기본 스타일 / CircularProgress default styles
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            // 기본 스타일 유지
          },
        },
      },

      // Snackbar 기본 스타일 / Snackbar default styles
      MuiSnackbar: {
        styleOverrides: {
          root: {
            // 기본 스타일 유지
          },
        },
      },

      // Stack 기본 스타일 / Stack default styles
      MuiStack: {
        styleOverrides: {
          root: {
            // 기본 스타일 유지
          },
        },
      },

      // Collapse 기본 스타일 / Collapse default styles
      MuiCollapse: {
        styleOverrides: {
          root: {
            // 기본 스타일 유지
          },
        },
      },

      // Divider 기본 스타일 / Divider default styles
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },

      // CardContent 기본 스타일 / CardContent default styles
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '12px',
            '&:last-child': {
              paddingBottom: '12px',
            },

            // 메시지 컨텐츠 / Message content
            '&.message-content': {
              '& p': {
                margin: 0,
                marginBottom: '8px',
              },
              '& p:last-child': {
                marginBottom: 0,
              },
              '& ul, & ol': {
                margin: 0,
                paddingLeft: '16px',
              },
              '& li': {
                marginBottom: '4px',
              },
              '& strong': {
                fontWeight: 600,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& code': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& pre': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '12px',
                borderRadius: '8px',
                overflow: 'auto',
                margin: '8px 0',
              },
            },
          },
        },
      },

      // CardActions 기본 스타일 / CardActions default styles
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
          },
        },
      },

      // AppBar 기본 스타일 / AppBar default styles
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: '0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
        },
      },

      // Tabs 기본 스타일 / Tabs default styles
      MuiTabs: {
        styleOverrides: {
          root: {
            // 기본 스타일 유지
          },
        },
      },

      // Tab 기본 스타일 / Tab default styles
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },

      // List 기본 스타일 / List default styles
      MuiList: {
        styleOverrides: {
          root: {
            // 기본 스타일 유지
          },
        },
      },

      // ListItem 기본 스타일 / ListItem default styles
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
          },
        },
      },

      // ListItemButton 기본 스타일 / ListItemButton default styles
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
    },
  },
  koKR
);

// 타이포그래피 상수 / Typography constants
// export const TYPOGRAPHY = {
//   FONT_SIZES: {
//     BASE: 14, // 기본 폰트 사이즈 / Base font size
//     H1: 21, // 1.5rem
//     H2: 17.5, // 1.25rem
//     H3: 15.75, // 1.125rem
//     H4: 14, // 1rem
//     H5: 12.25, // 0.875rem
//     H6: 10.5, // 0.75rem
//     BODY1: 14, // 1rem
//     BODY2: 12.25, // 0.875rem
//     BUTTON: 12.25, // 0.875rem
//     CAPTION: 10.5, // 0.75rem
//     OVERLINE: 10.5, // 0.75rem
//     SUBTITLE1: 14, // 1rem
//     SUBTITLE2: 12.25, // 0.875rem
//   },
//   FONT_WEIGHTS: {
//     LIGHT: 300,
//     REGULAR: 400,
//     MEDIUM: 500,
//     SEMIBOLD: 600,
//     BOLD: 700,
//   },
//   LINE_HEIGHTS: {
//     TIGHT: 1.2,
//     NORMAL: 1.3,
//     RELAXED: 1.4,
//     LOOSE: 1.5,
//   },
// } as const;
